import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { getUwByEnrollmentPeriodId } from 'api/operations/uwService';
import { UwResult } from 'types/uwService';
import { bpHistory } from 'global/bpHistory';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { overrideRates } from 'api/operations/productServiceV2';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types/Toast';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { UwRatesOverriderDelegate } from './uwRatesOverriderDelegate';

interface DomainParams {
  periodId: string;
  cartId: string;
  portfolioId: string;
  productLineId: ProductLineId;
}

export class OverrideUwRatesPageStore {
  private _domainParams: DomainParams;
  private _isLoading = true;
  private _uwResult: UwResult | null = null;

  public uwRatesOverriderDelegate: UwRatesOverriderDelegate = new UwRatesOverriderDelegate({ uwProducts: [] });
  public enrollmentModule = new EnrollmentPeriodModule();

  public async getInitialData(): Promise<void> {
    this.setLoading(true);
    await this.enrollmentModule.uploadEnrollmentPeriod(this._domainParams.periodId);
    const groupId = this.enrollmentModule.enrollmentCoreData?.groupId || '';
    const uwEligibility = await getUwByEnrollmentPeriodId(groupId, this._domainParams.periodId);

    runInAction(() => {
      const uwResult = uwEligibility.result.find(
        (resultByPLine) => resultByPLine.productLineId === this._domainParams.productLineId,
      );
      if (!uwResult) {
        throw new Error(`UW result not found for product line id: ${this._domainParams.productLineId}`);
      }
      this._uwResult = uwResult;
      this.uwRatesOverriderDelegate.createRateForms(
        uwResult.products.map((product) => ({
          ...product,
          portfolioId: this._domainParams.portfolioId,
          productId: product.id,
          bcIndex: -1, // -1 is the agreed bcIndex to store overwritten rates.
        })),
      );
      this.setLoading(false);
    });
  }

  public async overrideRates(): Promise<void> {
    this.setLoading(true);
    await overrideRates(this._domainParams.cartId, this.uwRatesOverriderDelegate.ratesOverrideData);
    toastManager.addToast('Rates overridden successfully!', ToastColorTypes.SUCCESS);
    bpHistory.push(
      `/review-submit/${this._domainParams.periodId}?groupId=${this.enrollmentModule.enrollmentCoreData?.groupId}`,
    );
  }

  public onBackRedirect(): void {
    bpHistory.push(
      `/review-submit/${this._domainParams.periodId}?groupId=${this.enrollmentModule.enrollmentCoreData?.groupId}`,
    );
  }

  public get uwResult(): UwResult | null {
    return this._uwResult;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get productLineId(): ProductLineId {
    return this._domainParams.productLineId;
  }

  public get canOverrideRates(): boolean {
    return this.uwRatesOverriderDelegate.rateForms.every((form) => form.formController.basicFieldsValidationsPassed);
  }

  public setLoading(status: boolean): void {
    this._isLoading = status;
  }

  constructor(params: DomainParams) {
    this._domainParams = params;
    makeObservable<OverrideUwRatesPageStore, '_isLoading' | '_domainParams' | '_uwResult'>(this, {
      _domainParams: observable,
      _isLoading: observable,
      _uwResult: observable,
      uwRatesOverriderDelegate: observable,
      getInitialData: action.bound,
      setLoading: action.bound,
      onBackRedirect: action.bound,
      overrideRates: action.bound,
      isLoading: computed,
      canOverrideRates: computed,
      uwResult: computed,
    });
  }
}

const OverrideUwRatesPageStoreContext = createContext(new OverrideUwRatesPageStore({} as DomainParams));

const useOverrideUwRatesPageStore = (): OverrideUwRatesPageStore => {
  return useContext(OverrideUwRatesPageStoreContext);
};

export { OverrideUwRatesPageStoreContext, useOverrideUwRatesPageStore };
